import React from 'react'
import { Document, Page, Text, View } from '@react-pdf/renderer'
import moment from 'moment'
import RecordSection from '../../patientRecords/Sharing/PDF/_RecordSection'
import InfoLine from '../../patientRecords/Sharing/PDF/_InfoLine'

const REGISTRATION_STATUS_LABEL = {
  not_registered: 'Not Registered',
  preparing_application: 'Preparing Application',
  applied: 'Applied',
  registered: 'Registered',
  exempt: 'Exempt',
  not_applicable: 'Not Applicable',
}

const DISCLAIMER =
  'This Statement of Purpose draft is prepared by the organisation as an aid to a CQC ' +
  'registration application. It is a pragmatic drafting aid based on the sections CQC’s ' +
  'registration application typically asks for, not a legally exhaustive replica of CQC’s ' +
  'current application form, and does not itself constitute a submitted or approved registration. ' +
  'Review the finished draft against CQC’s own guidance before submitting.'

const Row = ({ children }) => (
  <View
    style={{
      flexDirection: 'row',
      justifyContent: 'space-between',
      fontSize: 9,
      paddingVertical: 3,
      paddingHorizontal: 5,
      borderBottomWidth: 1,
      borderBottomColor: '#eee',
      borderBottomStyle: 'solid',
    }}
  >
    {children}
  </View>
)

const SopPDF = ({ pack }) => {
  const sop = pack.statementOfPurpose || {}

  return (
    <Document options={{ isEvalSupported: false }}>
      <Page size="A4" style={{ paddingVertical: 40, paddingHorizontal: 20, fontFamily: 'Helvetica' }}>
        <View style={{ flexDirection: 'column', gap: 2, paddingBottom: 10 }} wrap>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontSize: 14, color: 'grey' }}>{pack.orgName}</Text>
            <Text style={{ fontSize: 9, color: 'grey' }}>
              Generated {moment(pack.generatedAt).format('Do MMMM YYYY [at] HH:mm')}
            </Text>
          </View>

          <View style={{ flexDirection: 'column', alignItems: 'center', marginTop: 14, gap: 6 }}>
            <Text style={{ fontSize: 18, color: '#3c4e50' }}>Statement of Purpose (Draft)</Text>
            <Text style={{ fontSize: 11, color: '#5f047bff' }}>
              Status: {sop.status || 'draft'}
            </Text>
          </View>

          <View
            style={{
              marginVertical: 10,
              marginHorizontal: 20,
              padding: 10,
              borderStyle: 'solid',
              borderWidth: 1,
            }}
          >
            <Text style={{ fontSize: 8 }}>{DISCLAIMER}</Text>
          </View>

          <RecordSection title="Provider Details">
            <InfoLine label="Legal entity name" value={sop.providerDetails?.legalEntityName} />
            <InfoLine label="Legal entity type" value={sop.providerDetails?.legalEntityType} />
            <InfoLine label="Registered address" value={sop.providerDetails?.registeredAddress} />
            <InfoLine label="CQC Provider ID" value={sop.providerDetails?.cqcProviderId} />
          </RecordSection>

          {sop.aimsAndObjectives && (
            <RecordSection title="Aims and Objectives">
              <Text style={{ fontSize: 9, paddingHorizontal: 5 }}>{sop.aimsAndObjectives}</Text>
            </RecordSection>
          )}

          <RecordSection title="Regulated Activities">
            {(sop.regulatedActivities || []).length === 0 ? (
              <Text style={{ fontSize: 8, paddingHorizontal: 5, color: '#a33' }}>
                None recorded yet.
              </Text>
            ) : (
              sop.regulatedActivities.map((ra, i) => (
                <Row key={i}>
                  <Text>{ra.activityType}</Text>
                  <Text>{ra.narrative}</Text>
                </Row>
              ))
            )}
          </RecordSection>

          <RecordSection title="Locations">
            {(sop.locations || []).length === 0 ? (
              <Text style={{ fontSize: 8, paddingHorizontal: 5, color: '#a33' }}>
                None recorded yet.
              </Text>
            ) : (
              sop.locations.map((loc, i) => (
                <Row key={i}>
                  <Text>{loc.name}</Text>
                  <Text>{loc.address}</Text>
                </Row>
              ))
            )}
          </RecordSection>

          <RecordSection title="Registered Manager">
            <InfoLine label="Name" value={sop.registeredManager?.name} />
            <InfoLine label="Contact details" value={sop.registeredManager?.contactDetails} />
            <InfoLine label="Qualifications summary" value={sop.registeredManager?.qualificationsSummary} />
          </RecordSection>

          <RecordSection title="Nominated Individual">
            <InfoLine label="Name" value={sop.nominatedIndividual?.name} />
            <InfoLine label="Contact details" value={sop.nominatedIndividual?.contactDetails} />
          </RecordSection>

          <RecordSection title="Service User Bands">
            <Text style={{ fontSize: 9, paddingHorizontal: 5 }}>
              {(sop.serviceUserBands || []).length ? sop.serviceUserBands.join(', ') : 'None recorded yet.'}
            </Text>
          </RecordSection>

          {sop.safeguardingArrangementsSummary && (
            <RecordSection title="Safeguarding Arrangements">
              <Text style={{ fontSize: 9, paddingHorizontal: 5 }}>
                {sop.safeguardingArrangementsSummary}
              </Text>
            </RecordSection>
          )}

          <RecordSection title="Regulated Activities Register">
            {(pack.regulatedActivities || []).length === 0 ? (
              <Text style={{ fontSize: 8, paddingHorizontal: 5, color: '#a33' }}>
                No regulated activities recorded in the Registration Centre yet.
              </Text>
            ) : (
              pack.regulatedActivities.map((a) => (
                <Row key={a._id}>
                  <Text>{a.locationLabel} - {a.activityType}</Text>
                  <Text>{REGISTRATION_STATUS_LABEL[a.registrationStatus] || a.registrationStatus}</Text>
                </Row>
              ))
            )}
          </RecordSection>
        </View>

        <View
          style={{ position: 'absolute', bottom: 40, left: 20, right: 20, fontSize: 8, color: 'grey' }}
          render={({ pageNumber, totalPages }) =>
            pageNumber === totalPages && (
              <View style={{ flexDirection: 'column', gap: 2 }}>
                <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 9 }}>Powered by AmbuNet &#174;</Text>
                <Text>{DISCLAIMER}</Text>
              </View>
            )
          }
        />
      </Page>
    </Document>
  )
}

export default SopPDF
